<template>
  <v-card
    class="bc-very-light-grey bg-super-light-blue b-1 mnh-400"
    tile
  >
    <v-row
      class="mb-3"
      dense
      no-gutters
    >
      <v-col
        v-if="!readonly"
        @click="mode = 'search'"
        @keydown.enter="mode = 'search'"
        :class="mode == 'search' ? 'elevation-2' : 'elevation-0'"
        class="pointer"
      >
        <div
          :class="mode == 'search' ? 'bg-primary c-white' : 'bg-white c-black'"
          class="fw-500 py-6 d-flex align-center justify-center"
          data-cy="search-mode-button"
          data-testid="search-mode-button"
        >
          {{ $bct('Search') }}
        </div>
      </v-col>
      <v-col
        @click="mode = 'selected'"
        @keydown.enter="mode = 'selected'"
        :class="mode == 'selected' ? 'elevation-2' : 'elevation-0'"
        :cols="readonly ? '12' : '6'"
        class="pointer"
      >
        <div
          :class="mode == 'selected' ? 'bg-primary c-white' : 'bg-white c-black'"
          class="fw-500 py-6 d-flex align-center justify-center"
          data-cy="selected-mode-button"
          data-testid="favorites-selected-mode-button"
        >
          {{ $bct('Selected') }}{{ ` (${selectedCount})` }}
        </div>
      </v-col>
    </v-row>

    <div
      v-show="mode == 'selected'"
      class="pa-3"
    >
      <template v-if="providerIds.length == 0">
        <p
          class="fs-16 c-black pt-8"
          data-cy="selected-hint"
        >
          {{ $bct('You have not selected any sites. To add sites, click the Search tab.') }}
        </p>
      </template>

      <transition-group>
        <div
          v-for="(provider_id, index) in providerIds"
          :key="provider_id"
          class="mb-6 sortable-item"
        >
          <FavoriteCard
            @move="reorderProviders(index, index + $event)"
            @toggle="toggleInclusion(index, $event)"
            :active="true"
            :index="index"
            :length="providerIds.length"
            :ordered="allowOrder"
            :provider-id="provider_id"
            :readonly="readonly"
            class="my-4"
          />
        </div>
      </transition-group>
    </div>

    <div
      v-show="mode == 'search'"
      class="pa-3"
      mandatory
    >
      <v-tabs
        v-model="tab"
        @update:model-value="handleTabChange"
        bg-color="transparent"
      >
        <v-tab value="addressSearchTab">
          <span
            class="ls-normal tt-none fs-16 fw-500"
            data-cy="address-mode-button"
            data-testid="favorites-address-search-tab"
          >
            {{ $bct('Search by address') }}
          </span>
        </v-tab>
        <v-tab value="nameSearchTab">
          <span
            class="ls-normal tt-none fs-16 fw-500"
            data-cy="name-mode-button"
            data-testid="favorites-name-search-tab"
          >
            {{ $bct('Search by name') }}
          </span>
        </v-tab>
      </v-tabs>

      <v-card class="d-flex align-center pa-4 mb-4">
        <v-row>
          <v-col class="d-flex flex-grow-1 flex-shrink-0">
            <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledTextfield -->
            <v-text-field
              v-model="searchQuery"
              @keydown.enter="searchProviders()"
              ref="searchInput"
              data-cy="query-input"
              data-testid="favorites-query-text-field"
              prepend-inner-icon="search"
              autofocus
              hide-details
            />
          </v-col>
          <v-col class="d-flex justify-end flex-grow-0 flex-shrink-0">
            <v-btn
              @click="searchProviders()"
              :loading="processing"
              class="ms-3"
              color="primary"
              data-cy="search-button"
              data-testid="favorites-search-submit"
              size="x-large"
            >
              {{ $bct('Search') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-row
        v-if="subsidyProgram.selection_limit > 0"
        class="mb-3"
      >
        <v-col>
          <span class="fs-16 fw-800 c-primary">
            {{ $bct(`You can select ${subsidyProgram.selection_limit} total options`) }}
          </span>
        </v-col>
      </v-row>

      <v-window v-model="tab">
        <v-window-item value="addressSearchTab">
          <v-row
            v-if="search"
            class="d-flex align-center mb-1"
          >
            <v-col
              cols="12"
              sm="8"
            >
              <template
                v-if="
                  store.state.schemas[subsidyProgram.provider_schema_id].meta.openings.enabled &&
                  store.state.schemas[subsidyProgram.provider_schema_id].meta.openings.public
                "
              >
                <FilterMenu
                  @clear="filters.vacancy = null"
                  @close="searchProviders()"
                  :actions="true"
                  :active="filters.vacancy"
                  :active-classes="['bg-white', 'bc-primary', 'b-1']"
                  :inactive-classes="['bg-white']"
                  :outlined="false"
                  data-cy="vacancy-filter"
                  elevation="2"
                  tid="search_availability_filter"
                  title="Availability"
                  hide-clear-filter
                >
                  <template #card>
                    <v-checkbox
                      v-model="filters.vacancy"
                      class="mt-0 mb-0"
                      color="primary"
                      false-icon="check_box_outline_blank"
                      true-icon="check_box"
                      hide-details
                    >
                      <template #label>
                        <span
                          class="c-black"
                          data-cy="availability-checkmark"
                        >
                          {{ $bct('Only show providers that have reported availability') }}
                        </span>
                      </template>
                    </v-checkbox>
                  </template>
                </FilterMenu>
              </template>

              <FilterMenu
                v-if="!store.state.pages?.Search?.default_age_group?.enabled"
                @close="searchProviders()"
                :actions="true"
                :active="true"
                :active-classes="['bg-white', 'bc-primary', 'b-1']"
                :inactive-classes="['bg-white']"
                :outlined="false"
                data-cy="age-filter"
                elevation="2"
                tid="search_ages_filter"
                title="Age"
                hide-clear-filter
              >
                <template #card>
                  <LabeledVolume
                    v-for="(item, index) in ageGroups"
                    @decrease="modifyCount(index, -1)"
                    @increase="modifyCount(index, 1)"
                    :key="index"
                    ref="childVolume"
                    :count="item.count"
                    :dense="true"
                    :subtitle="item.subtitle || item.subtext"
                    :title="item.title || item.text"
                  />
                </template>
              </FilterMenu>

              <v-btn
                v-if="store.state.pages?.Search?.features?.enable_more_filters"
                @click="moreFiltersDialogIsVisible = true"
                :class="moreFiltersButtonClass"
                class="focus-visible"
                data-cy="more-filters-button"
                data-tid="search_more_filter"
                elevation="2"
                rounded
              >
                <span
                  class="bc-body c-black d-none d-md-flex fw-500 fs-14"
                  data-cy="more-filters"
                >
                  {{ $bct('More filters') }}
                </span>
                <v-icon
                  class="d-flex d-md-none fw-500"
                  icon="more_horiz"
                />
              </v-btn>

              <v-dialog
                v-model="moreFiltersDialogIsVisible"
                width="600px"
                scrollable
              >
                <v-card>
                  <v-card-title class="mb-n-3 pb-0">
                    {{ $bct('More filters') }}
                  </v-card-title>
                  <v-card-text class="pt-0 pb-6">
                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .program_types.filterable
                      "
                    >
                      <v-divider class="my-6" />
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{
                            $bct(
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.program_types.alias || 'Program types',
                            )
                          }}
                        </v-col>
                      </v-row>
                      <v-row
                        class="mb-4"
                        dense
                      >
                        <v-col>
                          <v-checkbox
                            v-for="type in store.state.schemas[subsidyProgram.provider_schema_id]
                              .definition.properties.program_types.items.enum"
                            v-model="filters.program_types"
                            :key="type"
                            :label="capitalize($bct(type))"
                            :value="type"
                            class="c-black mt-0"
                            color="primary"
                            hide-details
                            multiple
                          />
                        </v-col>
                      </v-row>
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .categories.filterable
                      "
                    >
                      <v-divider class="my-6" />
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{
                            $bct(
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.categories.alias || 'Categories',
                            )
                          }}
                        </v-col>
                      </v-row>

                      <v-row
                        class="mb-4"
                        dense
                      >
                        <v-col>
                          <div
                            v-if="
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.categories.items?.enum?.length < 20
                            "
                          >
                            <v-checkbox
                              v-for="type in store.state.schemas[subsidyProgram.provider_schema_id]
                                .definition.properties.categories.items.enum"
                              v-model="filters.categories"
                              :key="type"
                              :label="capitalize($bct(type))"
                              :value="type"
                              class="c-black mt-0"
                              color="primary"
                              hide-details
                              multiple
                            />
                          </div>
                          <div v-else>
                            <v-autocomplete
                              v-model="filters.categories"
                              :items="
                                store.state.schemas[subsidyProgram.provider_schema_id].definition
                                  .properties.categories.items.enum
                              "
                              :label="$bct('Select all that apply')"
                              variant="filled"
                              chips
                              closable-chips
                              hide-details
                              multiple
                            />
                          </div>
                        </v-col>
                      </v-row>
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .program_season.filterable
                      "
                    >
                      <v-divider class="my-6" />
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{
                            $bct(
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.program_season.alias || 'Season',
                            )
                          }}
                        </v-col>
                      </v-row>
                      <v-row
                        class="mb-4"
                        dense
                      >
                        <v-col>
                          <v-radio-group
                            v-model="filters.program_season"
                            class="mt-0"
                            hide-details
                          >
                            <v-radio
                              v-for="type in store.state.schemas[subsidyProgram.provider_schema_id]
                                .definition.properties.program_season.enum"
                              :key="type"
                              :label="capitalize($bct(type))"
                              :value="type"
                              class="c-black mt-0"
                              color="primary"
                              hide-details
                            />
                          </v-radio-group>
                        </v-col>
                      </v-row>
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .quality_rating_score.filterable
                      "
                    >
                      <v-divider class="my-6" />
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{ $bct('Quality rating') }}
                        </v-col>
                      </v-row>

                      <v-row dense>
                        <v-col>
                          <div v-if="store.state.site.ratings.Description">
                            <div class="c-light-black fs-15 pt-2 pb-4">
                              <span
                                v-text="store.state.site.ratings.Description"
                                class="c-light-black fs-15"
                              />
                              <a
                                v-if="store.state.site.ratings.Link"
                                :href="store.state.site.ratings.Link"
                                class="c-primary fs-15 ms-1 underlined"
                                target="_blank"
                              >
                                {{ $bct('Learn more') }}
                              </a>
                            </div>
                          </div>
                          <v-radio-group
                            v-model="filters.minimum_quality_rating"
                            class="mt-0 mb-2"
                            hide-details
                          >
                            <div
                              v-for="(level, index) in store.state.site.ratings.text
                                .slice()
                                .reverse()"
                              :key="index"
                            >
                              <RadioWithContext
                                :index="index"
                                :item="level"
                                :suffix="index > 0 ? 'or higher' : null"
                                :value="store.state.site.ratings.max - index"
                              />
                              <v-divider
                                v-if="level.subtitle"
                                v-show="index + 1 < store.state.site.ratings.text.length"
                                class="my-3"
                              />
                            </div>
                          </v-radio-group>
                          <v-btn
                            v-show="filters.minimum_quality_rating"
                            @click="filters.minimum_quality_rating = null"
                            color="primary"
                            size="small"
                          >
                            {{ $bct('Clear filter') }}
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .hours.filterable
                      "
                    >
                      <v-divider class="my-6" />
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{ $bct('Hours') }}
                        </v-col>
                      </v-row>
                      <v-row>
                        <LabeledOpenClose
                          v-model="hoursRange"
                          close-title="Pickup time"
                          open-title="Drop-off time"
                          title-class="fs-14"
                        />
                      </v-row>
                      <div class="c-gray fs-15 mt-2 mb-3 mxw-400">
                        {{
                          $bct(
                            'We’ll show you results that offer drop-off at the time requested or earlier, and pick-up at the time requested or later',
                          )
                        }}
                      </div>
                      <v-row dense>
                        <v-col
                          class="fw-600 fs-14"
                          cols="12"
                        >
                          {{ $bct('Days of the week') }}
                        </v-col>
                        <v-col cols="12">
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Monday')"
                            class="mt-0"
                            density="compact"
                            value="0"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Tuesday')"
                            class="mt-2"
                            density="compact"
                            value="1"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Wednesday')"
                            class="mt-2"
                            density="compact"
                            value="2"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Thursday')"
                            class="mt-2"
                            density="compact"
                            value="3"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Friday')"
                            class="mt-2"
                            density="compact"
                            value="4"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Saturday')"
                            class="mt-2"
                            density="compact"
                            value="5"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Sunday')"
                            class="mt-2"
                            density="compact"
                            value="6"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                    </div>

                    <SearchMoreFilters :value="filters" />
                  </v-card-text>
                  <v-divider />
                  <v-card-actions class="pt-2">
                    <v-btn
                      @click="clearMoreFilters()"
                      color="primary"
                      data-cy="clear-filters"
                      variant="text"
                    >
                      {{ $bct('Clear filters') }}
                    </v-btn>
                    <v-spacer />
                    <v-btn
                      @click="searchProviders()"
                      color="primary"
                      data-cy="apply-filter"
                    >
                      {{ $bct('Apply') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-col>
            <v-col
              class="ta-right"
              cols="12"
              sm="4"
            >
              <v-btn
                @click="toggleResultsMode"
                class="bg-white focus-very-visible"
                color="primary"
                data-cy="results-mode-button"
                variant="outlined"
              >
                <v-icon
                  v-show="resultsMode == 'map'"
                  icon="west"
                  start
                />
                <span>
                  {{ $bct(resultsMode == 'map' ? 'List view' : 'Map view') }}
                </span>
                <v-icon
                  v-show="resultsMode == 'list'"
                  icon="east"
                  end
                />
              </v-btn>
            </v-col>
          </v-row>

          <template v-if="search">
            <div v-show="resultsMode == 'map'">
              <div class="w-100pc ta-right focus-very-visible va-top h-400">
                <GoogleMap
                  ref="mapRef"
                  :center="mapCenter"
                  :max-zoom="15"
                  :options="{
                    draggable: true,
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                    disableDefaultUi: true,
                  }"
                  :zoom="zoom"
                  style="width: 100%; height: 100%"
                >
                  <MatchMarker
                    v-for="match in matches"
                    @click="handleMarkerClick(match)"
                    :key="match.id"
                    :match="match"
                    :selected="match == selectedMatch"
                    data-cy="match-marker"
                  />
                </GoogleMap>
              </div>
              <div v-if="selectedMatch">
                <SimpleProviderCard
                  @open="openProvider(selectedMatch.id)"
                  @toggle="toggleSelection(selectedMatch, $event)"
                  :key="selectedMatch.id"
                  :active="providerIds.includes(selectedMatch.id)"
                  :provider="selectedMatch"
                  :selectable="belowSelectionLimit"
                />
              </div>
            </div>

            <div v-show="resultsMode == 'list'">
              <SimpleProviderCard
                v-for="provider in matches"
                @open="openProvider(provider.id)"
                @toggle="toggleSelection(provider, $event)"
                :key="provider.id"
                :active="providerIds.includes(provider.id)"
                :provider="provider"
                :selectable="belowSelectionLimit"
                class="my-4"
              />
            </div>
          </template>

          <p
            v-if="tab === 'addressSearchTab' && matches.length === 0"
            class="fs-14 mt-2"
            data-cy="results-hint"
          >
            <span class="me-1">
              {{ $bct('Search by your address to see results near you.') }}
            </span>
          </p>
          <v-btn
            v-if="!providerIds?.length > 0 && previousSelections"
            @click="copyPreferences"
            color="primary"
          >
            <v-icon
              class="me-2"
              icon="redo"
            />
            <span>{{ $bct('Copy selections from') }}{{ `: ${previousChild.name}` }}</span>
          </v-btn>

          <v-card
            v-show="!processing && pages > 1"
            class="bottom-0 elevation-0 bg-super-light-blue w-100pc pb-2 pt-1"
            tile
          >
            <v-pagination
              @update:model-value="handlePageChange"
              :length="pages"
              :model-value="page"
              :total-visible="8"
              class="custom"
              rounded
            />
          </v-card>
        </v-window-item>

        <v-window-item value="nameSearchTab">
          <SimpleProviderCard
            v-for="provider in matches"
            @open="openProvider(provider.id)"
            @toggle="toggleSelection(provider, $event)"
            :key="provider.id"
            :active="providerIds.includes(provider.id)"
            :provider="provider"
            :selectable="belowSelectionLimit"
            class="my-4"
          />

          <p
            v-if="tab === 'nameSearchTab' && matches.length === 0"
            class="fs-14 mt-2"
            data-cy="results-hint"
          >
            {{ $bct('Search by the name of a provider to see results.') }}
          </p>

          <template v-if="searched && !processing && matches.length == 0">
            <p
              class="fs-16 c-black pt-8 px-2"
              data-cy="no-results-hint"
            >
              {{ $bct('No results found') }}
            </p>
          </template>
        </v-window-item>
      </v-window>
    </div>

    <ContentDialog
      ref="providerDialogRef"
      :max-width="800"
    >
      <template #content="{ data }">
        <ProviderMatch
          v-if="data"
          :match="data"
          :outlined="false"
          :schema="store.state.schemas[subsidyProgram.provider_schema_id]"
        />
      </template>
    </ContentDialog>
  </v-card>
</template>

<script setup>
import { GoogleMap } from 'vue3-google-map';
import { scrollTo } from 'vuetify/lib/composables/goto';
import { useStore } from 'vuex';

import { capitalize } from '@/plugins/filters';
import ContentDialog from '@/shared/components/ContentDialog.vue';
import { Coord } from '@/shared/services/coord';
import FavoriteCard from '@/parent/components/favorites/FavoriteCard.vue';
import FilterMenu from '@/shared/components/form/FilterMenu.vue';
import LabeledOpenClose from '@/shared/components/form/LabeledOpenClose.vue';
import LabeledVolume from '@/shared/components/form/LabeledVolume.vue';
import MatchMarker from '@/shared/components/map/MatchMarker.vue';
import ProviderMatch from '@/shared/components/search/ProviderMatch.vue';
import RadioWithContext from '@/shared/components/form/RadioWithContext.vue';
import SearchMoreFilters from '@/shared/components/search/MoreFilters.vue';
import SimpleProviderCard from '@/shared/components/SimpleProviderCard.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const { api: Api } = useApi();
const eventBus = useEventBus();
const store = useStore();

const $bct = inject('translator');

const emit = defineEmits(['update-provider-ids', 'update-excluded-provider-ids']);

const props = defineProps({
  allowOrder: {
    type: Boolean,
    default: false,
  },
  childData: {
    type: Object,
    default: null,
  },
  defaultQuery: {
    type: String,
    default: null,
  },
  initialMode: {
    type: String,
    default: null,
  },
  previousChild: {
    type: Object,
    default: null,
  },
  previousSubsidy: {
    type: Object,
    default: null,
  },
  providerIds: {
    type: Array,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  subsidy: {
    type: Object,
    default: null,
  },
  subsidies: {
    type: Array,
    default: null,
  },
  subsidyProgram: {
    type: Object,
    default: null,
  },
});

const children = ref([]);
const filters = ref({
  categories: [],
  custom: {},
  dropoff: null,
  highlights: {},
  minimum_quality_rating: null,
  payment_subsidies_accepted: [],
  pickup: null,
  program_season: null,
  program_types: [],
  schema_id: props.subsidyProgram?.provider_schema_id,
  vacancy: null,
  week_days: [],
});
const hoursRange = ref([900, 1600]);
const map = ref(null);
const mapRef = ref(null);
const mode = ref(props.initialMode || 'search');
const moreFiltersDialogIsVisible = ref(false);
const page = ref(1);
const pages = ref(null);
const processing = ref(false);
const providerDialogRef = ref(null);
const matches = ref([]);
const resultsMode = ref('list');
const searchCounter = ref(0);
const search = ref(null);
const searched = ref(false);
const searchQuery = ref(props.defaultQuery);
const selectedMatch = ref(null);
const tab = ref('addressSearchTab');
const zoom = ref(7);

const lastAddressSearchState = ref({
  searchQuery: null,
  search: null,
  matches: [],
  searched: false,
  page: null,
  pages: null,
});

const lastNameSearchState = ref({
  searchQuery: null,
  search: null,
  matches: [],
  searched: false,
  page: null,
  pages: null,
});

const ageGroups = computed(() => {
  if (store.state.pages?.Search?.default_age_group?.enabled) {
    const group = store.state.pages.Search.default_age_group;
    return [
      {
        count: 1,
        group: group.name,
        max: group.max,
        min: group.min,
        title: group.name,
      },
    ];
  }
  return JSON.parse(JSON.stringify(store.state.ageGroups.filter((ag) => !ag.hidden))).map((age) =>
    Object.assign(age, { count: 0, group: age.name }),
  );
});

const belowSelectionLimit = computed(() => {
  if (props.subsidyProgram?.selection_limit === 0) return true;
  return props.subsidyProgram?.selection_limit > props.providerIds?.length;
});

const mapCenter = computed(() => {
  if (!search.value?.center) return {};
  const coord = new Coord(search.value.center);
  return coord.lat !== null && coord.lng !== null ? coord : {};
});

const mapOrigin = computed(() => {
  if (!search.value?.origin?.latitude) return null;
  const coord = new Coord(search.value.origin);
  return coord.lat !== null && coord.lng !== null ? coord : null;
});

const moreFiltersActive = computed(() => {
  return (
    filters.value.categories.length > 0 ||
    Object.values(filters.value.highlights).some((item) => item.length > 0) ||
    Object.values(filters.value.custom).some((item) => item && item.length > 0) ||
    filters.value.minimum_quality_rating ||
    filters.value.payment_subsidies_accepted.length > 0 ||
    filters.value.program_season ||
    filters.value.program_types.length > 0 ||
    !!filters.value.subsidy
  );
});

const moreFiltersButtonClass = computed(() => {
  let c = ['me-2'];
  if (moreFiltersActive.value) {
    c = c.concat(['bg-white', 'bc-primary', 'b-1']);
  } else {
    c = c.concat(['bg-white', 'bc-very-light-gray']);
  }
  return c.join(' ');
});

const previousSelections = computed(() => {
  if (props.previousChild && props.previousSubsidy) {
    if (props.previousSubsidy.provider_ids.length > 0) {
      return props.previousSubsidy.provider_ids;
    }
  }
  return null;
});

const selectedCount = computed(() => {
  return props.providerIds?.length;
});

function handleTabChange(newTab) {
  // Save current state based on which tab we're at
  // (this is not a watch, so tab === newTab and we need to save opposite search method)
  if (newTab === 'nameSearchTab') {
    lastAddressSearchState.value = {
      searchQuery: searchQuery.value,
      search: search.value,
      matches: [...matches.value],
      searched: searched.value,
      page: page.value,
      pages: pages.value,
    };
  } else if (newTab === 'addressSearchTab') {
    lastNameSearchState.value = {
      searchQuery: searchQuery.value,
      search: search.value,
      matches: [...matches.value],
      searched: searched.value,
      page: page.value,
      pages: pages.value,
    };
  }

  // Always reset current state when switching tabs
  matches.value = [];
  processing.value = false;
  searched.value = false;
  searchQuery.value = null;
  search.value = null;
  pages.value = null;

  // Restore state based on which tab we're entering
  if (newTab === 'addressSearchTab' && lastAddressSearchState.value.searchQuery) {
    searchQuery.value = lastAddressSearchState.value.searchQuery;
    search.value = lastAddressSearchState.value.search;
    matches.value = [...lastAddressSearchState.value.matches];
    searched.value = lastAddressSearchState.value.searched;
    page.value = lastAddressSearchState.value.page;
    pages.value = lastAddressSearchState.value.pages;
  } else if (newTab === 'nameSearchTab' && lastNameSearchState.value.searchQuery) {
    searchQuery.value = lastNameSearchState.value.searchQuery;
    search.value = lastNameSearchState.value.search;
    matches.value = [...lastNameSearchState.value.matches];
    searched.value = lastNameSearchState.value.searched;
    page.value = lastNameSearchState.value.page;
    pages.value = lastNameSearchState.value.pages;
  }
}

watch(resultsMode, (newVal) => {
  if (newVal === 'map') {
    setTimeout(() => {
      fitMap();
    }, 100);
  }
});

onMounted(() => {
  window.vueGoogleMapsInit?.(google);
});

async function handlePageChange(newPage) {
  page.value = newPage;
  if (resultsMode.value === 'list') {
    await scrollTo(0);
  }
  loadMatches();
}

function clearHours() {
  hoursRange.value = [900, 1600];
  filters.value.dropoff = null;
  filters.value.pickup = null;
  filters.value.week_days = [];
}

function clearMoreFilters() {
  clearHours();
  filters.value.categories = [];
  filters.value.custom = {};
  filters.value.highlights = {};
  filters.value.minimum_quality_rating = null;
  filters.value.payment_subsidies_accepted = [];
  filters.value.program_season = null;
  filters.value.program_types = [];
  moreFiltersDialogIsVisible.value = false;
  searchProviders();
}

function copyPreferences() {
  const newProviderIds = JSON.parse(JSON.stringify(previousSelections.value));
  emit('update-provider-ids', newProviderIds);
  mode.value = 'selected';
}

function fitMap() {
  if (matches.value.length <= 1) return;

  const bounds = new google.maps.LatLngBounds();
  if (mapOrigin.value) {
    bounds.extend(mapOrigin.value);
  }

  for (let index = 0; index < matches.value.length; index += 1) {
    const coord = new Coord(matches.value[index]);
    if (coord.lat !== null && coord.lng !== null) {
      bounds.extend(coord);
    }
  }
}

function handleMarkerClick(match) {
  selectedMatch.value = match;
}

function loadMatches() {
  Api.match.index(
    search.value.id,
    { page: page.value, subsidy_program_id: props.subsidyProgram.id },
    (searchResp) => {
      processing.value = false;
      matches.value = searchResp.data;
      pages.value = parseInt(searchResp.headers['x-page-count'] || '0', 10);

      if (!map.value) {
        mapRef.value?.$mapPromise?.then((mapResult) => {
          map.value = mapResult;
        });
      }

      if (resultsMode.value === 'map') fitMap();
    },
  );
}

function modifyCount(index, addition) {
  ageGroups.value[index].count = ageGroups.value[index].count + addition;
}

function reorderProviders(fromIndex, toIndex) {
  const newProviderIds = [...props.providerIds];
  const element = newProviderIds[fromIndex];
  newProviderIds.splice(fromIndex, 1);
  newProviderIds.splice(toIndex, 0, element);
  emit('update-provider-ids', newProviderIds);
}

function getMonthsAgo(dateFrom) {
  const dateTo = new Date();
  return (
    dateTo.getMonth() - dateFrom.getMonth() + 12 * (dateTo.getFullYear() - dateFrom.getFullYear())
  );
}

function openProvider(providerId) {
  Api.publicApi.provider.get(providerId, (resp) => {
    providerDialogRef.value.open(resp.data);
  });
}

function searchProviders() {
  processing.value = true;
  searchCounter.value += 1;
  const counter = searchCounter.value;
  moreFiltersDialogIsVisible.value = false;

  if (!searchQuery.value) {
    matches.value = [];
    processing.value = false;
    pages.value = null;
  } else if (tab.value === 'addressSearchTab') {
    const defaultAgeGroup = store.state.pages?.Search?.default_age_group;
    if (defaultAgeGroup?.enabled) {
      children.value = [
        {
          count: 1,
          group: defaultAgeGroup.name,
          max: defaultAgeGroup.max,
          min: defaultAgeGroup.min,
          title: defaultAgeGroup.name,
          included: true,
        },
      ];
    } else {
      if (children.value.length === 0) {
        const months = getMonthsAgo(new Date(props.childData.dob));
        const ageGroup = ageGroups.value.find((ag) => ag.min <= months && ag.max >= months);
        if (ageGroup) ageGroup.count = 1;
      }
      children.value = ageGroups.value.filter((ag) => ag.count > 0);
    }

    if (filters.value.week_days.length > 0) {
      [filters.value.dropoff, filters.value.pickup] = hoursRange.value;
    } else {
      filters.value.dropoff = null;
      filters.value.pickup = null;
    }

    searched.value = true;
    Api.search.create(
      {
        categories: filters.value.categories,
        children: children.value,
        custom: filters.value.custom,
        dropoff: filters.value.dropoff,
        highlights: filters.value.highlights,
        location_type: 'home',
        minimum_quality_rating: filters.value.minimum_quality_rating,
        payment_subsidies_accepted: filters.value.payment_subsidies_accepted,
        pickup: filters.value.pickup,
        program_season: filters.value.program_season,
        program_types: filters.value.program_types,
        schema_id: props.subsidyProgram.provider_schema_id,
        unformatted_origin: searchQuery.value,
        vacancy: filters.value.vacancy,
        week_days: filters.value.week_days,
      },
      (resp) => {
        search.value = resp.data;
        page.value = 1;
        loadMatches();
      },
      (err) => {
        if (err.response && err.response.data && err.response.data.errors) {
          eventBus.chime(err.response.data.errors[0]);
        } else {
          eventBus.chime('Unknown error occurred');
        }
        processing.value = false;
      },
    );
  } else if (tab.value === 'nameSearchTab') {
    searched.value = true;
    Api.publicApi.provider.index(
      {
        query: searchQuery.value,
        schema_id: props.subsidyProgram.provider_schema_id,
        subsidy_program_id: props.subsidyProgram.id,
      },
      (resp) => {
        if (counter === searchCounter.value) {
          matches.value = resp.data;
          processing.value = false;
        }
      },
    );
  }
}

function toggleInclusion(index, newVal) {
  if (newVal) {
    const movedId = props.subsidy.excluded_provider_ids[index];
    const newProviderIds = [...props.providerIds, movedId];
    const newExcludedIds = props.subsidy.excluded_provider_ids.filter((_, i) => i !== index);
    emit('update-provider-ids', newProviderIds);
    emit('update-excluded-provider-ids', newExcludedIds);
  } else {
    const movedId = props.providerIds[index];
    const newExcludedIds = [...props.subsidy.excluded_provider_ids, movedId];
    const newProviderIds = props.providerIds.filter((_, i) => i !== index);
    emit('update-provider-ids', newProviderIds);
    emit('update-excluded-provider-ids', newExcludedIds);
  }
}

function toggleResultsMode() {
  if (resultsMode.value === 'list') resultsMode.value = 'map';
  else resultsMode.value = 'list';
}

function toggleSelection(provider, newVal) {
  if (newVal) {
    if (!props.providerIds.includes(provider.id)) {
      const newProviderIds = [...props.providerIds, provider.id];
      emit('update-provider-ids', newProviderIds);

      if (
        props.subsidyProgram.selection_limit > 0 &&
        props.subsidyProgram.selection_limit === newProviderIds.length
      ) {
        eventBus.chime(
          'You reached your selection limit. Deselect another option if you want to add more.',
        );
      }
    }
  } else if (props.providerIds.includes(provider.id)) {
    const newProviderIds = props.providerIds.filter((id) => id !== provider.id);
    emit('update-provider-ids', newProviderIds);
  }
}
</script>
